import { ApolloQueryResult } from '@apollo/client';
import { InvoiceStatus, SageInvoiceStatus } from '@prisma/client';
import { GetSalesInvoice } from 'types/graphql';

import { useQuery } from '@redwoodjs/web';

import { GET_SALES_INVOICE } from 'src/graphql/queries/getSalesInvoice.query';
import { onError } from 'src/utils/utils';

type UseSalesInvoiceProps = {
    id: string;
};

type UseSalesInvoiceResponse = {
    salesInvoice: GetSalesInvoice['invoice'];
    refetchSalesInvoice: (
        variables?: Partial<GraphQLOperationVariables>
    ) => Promise<ApolloQueryResult<GetSalesInvoice>>;
    isSalesInvoiceProcessing: boolean;
    isSalesInvoiceCreateFailed: boolean;
    isSalesInvoiceReleaseFailed: boolean;
    isSalesInvoiceEmailFailed: boolean;
    isAllowedToAllocatePaymentToSalesInvoice: boolean;
};

export function useSalesInvoice({ id }: UseSalesInvoiceProps): UseSalesInvoiceResponse {
    const { data: salesInvoiceData, refetch: refetchSalesInvoice } = useQuery<GetSalesInvoice>(
        GET_SALES_INVOICE,
        {
            fetchPolicy: 'cache-and-network',
            skip: !id,
            variables: { id },
            onError,
        }
    );

    const salesInvoice = salesInvoiceData?.invoice;

    const isSalesInvoiceProcessing = salesInvoice?.status === InvoiceStatus.PROCESSING;

    const isSalesInvoiceCreateFailed = salesInvoice?.status === InvoiceStatus.FAILED;

    const isSalesInvoiceReleaseFailed =
        salesInvoice?.status === InvoiceStatus.PARTIALLY_FAILED &&
        salesInvoice?.sageInvoiceStatus === SageInvoiceStatus.DRAFT;

    const isSalesInvoiceEmailFailed =
        salesInvoice?.status === InvoiceStatus.PARTIALLY_FAILED &&
        salesInvoice?.sageInvoiceStatus === SageInvoiceStatus.UNPAID;

    const isAllowedToAllocatePaymentToSalesInvoice =
        salesInvoice?.status === InvoiceStatus.PARTIALLY_PAID ||
        salesInvoice?.status === InvoiceStatus.UNPAID;

    const isAllowedToAllocatePaymentToSalesInvoiceWhileOnlyEmailFailed =
        isAllowedToAllocatePaymentToSalesInvoice ||
        (isSalesInvoiceEmailFailed && !isSalesInvoiceReleaseFailed);

    return {
        salesInvoice,
        refetchSalesInvoice,
        isSalesInvoiceProcessing,
        isSalesInvoiceCreateFailed,
        isSalesInvoiceReleaseFailed,
        isSalesInvoiceEmailFailed,
        isAllowedToAllocatePaymentToSalesInvoice:
            isAllowedToAllocatePaymentToSalesInvoice ||
            isAllowedToAllocatePaymentToSalesInvoiceWhileOnlyEmailFailed,
    };
}

export default useSalesInvoice;
